import { Badge, Tooltip } from '@/design-system/components/ui';

import { PROTECTION_STATUS_DISPLAY, type ProtectionStatus } from '../domain/types';

export function formatProtectionDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export function getDaysBetween(from: string, to: string): number {
  const start = new Date(from);
  const end = new Date(to);
  return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

interface ProtectionBadgeProps {
  status: ProtectionStatus;
  /** ISO 8601. The backend only sends this alongside a non-null `status`, so the two travel together. */
  expiresAt: string;
}

/**
 * Badge for the derived protection status.
 *
 * Independent of `TierProtection[]` (the shield icon) — that array is empty once a protection
 * lapses, which is exactly the `expired` case, so the tooltip is built from `expiresAt` alone.
 * An affiliate can carry one signal without the other; never derive one from the other.
 */
export function ProtectionBadge({ status, expiresAt }: ProtectionBadgeProps) {
  const display = PROTECTION_STATUS_DISPLAY[status];
  const formattedDate = formatProtectionDate(expiresAt);
  const daysLeft = getDaysBetween(new Date().toISOString(), expiresAt);

  // `expired` leads with the date because `getDaysBetween` clamps to 0 for anything past. It says
  // "lapsed" rather than naming a tier: after expiry an affiliate lands on their audience-backed
  // effective tier, which is often not the default.
  const tooltip =
    status === 'expired'
      ? `Protection lapsed on ${formattedDate}`
      : `Protection expires ${formattedDate} (${daysLeft} day${daysLeft !== 1 ? 's' : ''} left)`;

  // Table rows are clickable, so the badge swallows its own clicks. The handler sits on `Badge`
  // rather than the wrapper: a `<span>` carrying an onClick reads as an interactive element it is
  // not, and `role="button"` would be a lie — it suppresses an ancestor's behaviour, it does not
  // add its own. The wrapper stays a plain layout/tooltip-trigger node.
  return (
    <Tooltip arrow side="top" content={tooltip}>
      <span className="tw:inline-flex tw:items-center">
        <Badge variant={display.variant} size="sm" onClick={(e) => e.stopPropagation()}>
          {display.label}
        </Badge>
      </span>
    </Tooltip>
  );
}
